import React from 'react';
import { css, keyframes, styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';

export type Props = {
	numLinks: number;
};
export const SkeletonRelatedWorkList = ({ numLinks }: Props) => (
	<TableWrapper>
		<RelatedWorkItemWrapper>
			<SkeletonRelatedWorkRowWrapper>
				{[...Array(numLinks)].map((_, i) => (
					<RelatedWorkItemWrapper key={i}>
						<SkeletonRelatedWorkIcon opacity={1 - i * 0.2} />
						<SkeletonRelatedWorkItemLarge opacity={1 - i * 0.2} />
					</RelatedWorkItemWrapper>
				))}
			</SkeletonRelatedWorkRowWrapper>
			<SkeletonRelatedWorkRowWrapper>
				{[...Array(numLinks)].map((_, i) => (
					<SkeletonRelatedWorkItemSmall key={i} opacity={1 - i * 0.2} />
				))}
			</SkeletonRelatedWorkRowWrapper>
		</RelatedWorkItemWrapper>
	</TableWrapper>
);

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '1700px 0',
	},
});

interface SkeletonRelatedWorkItemProps {
	opacity?: number;
}

const SkeletonRelatedWorkItemStyles = css<SkeletonRelatedWorkItemProps>({
	animationDelay: "'0s'",
	animationDuration: '2s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',

	backgroundImage: `linear-gradient( to right, ${token('color.skeleton')} 10%, ${token('color.skeleton.subtle')} 30%, ${token('color.skeleton')} 50% )`,
	backgroundRepeat: 'no-repeat',
	height: '22px',

	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 0.5}px`,
});

const verticalMargin = 15;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonRelatedWorkIcon = styled.div<SkeletonRelatedWorkItemProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	SkeletonRelatedWorkItemStyles,
	{
		animationName: shimmer,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 2.75}px`,
		margin: `${verticalMargin}px ${token('space.150')} ${verticalMargin}px ${token('space.200')}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: (props: SkeletonRelatedWorkItemProps) => (props.opacity ? props.opacity : '1'),
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonRelatedWorkItemLarge = styled.div<SkeletonRelatedWorkItemProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	SkeletonRelatedWorkItemStyles,
	{
		animationName: shimmer,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 26.5}px`,

		margin: `${verticalMargin}px 0 ${verticalMargin}px 0`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: (props: SkeletonRelatedWorkItemProps) => (props.opacity ? props.opacity : '1'),
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonRelatedWorkItemSmall = styled.div<SkeletonRelatedWorkItemProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	SkeletonRelatedWorkItemStyles,
	{
		animationName: shimmer,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 6.5}px`,
		margin: `${verticalMargin}px ${token('space.800')} ${verticalMargin}px 0`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: (props: SkeletonRelatedWorkItemProps) => (props.opacity ? props.opacity : '1'),
	},
);
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableWrapper = styled.div({
	width: '100%',
	display: 'flex',
	position: 'relative',
	flexDirection: 'column',
	paddingTop: token('space.150'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonRelatedWorkRowWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	paddingLeft: token('space.050'),
	rowGap: token('space.050'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RelatedWorkItemWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
});
